"use client"

import { create } from "zustand"
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore"
import type { ViewContext } from "@/schemas/wilsonViewContext.schema"
import { capturePageContext } from "@/components/desktop/screenReadLoader"
import { takeHighlightedText } from "@/hooks/useHighlightedText"
import { askInShipmentPanel } from "@/stores/shipmentChatPanelStore"

export const CHAT_WINDOW_TYPE = "wilson"

export interface PendingAsk {
    id: string
    text: string
    viewContext?: ViewContext
}

export interface ChatRequest {
    question?: string
    threadId?: string
    viewContext?: ViewContext
}

export interface RecordThread {
    claimId: string
    documentId: string
    title: string
    isShipment: boolean
    focusChat: () => void
}

interface ChatAppState {
    focusRequest: number
    pendingAsk: PendingAsk | null
    pendingThreadId: string | null
    recordSlot: HTMLElement | null
    stagedRecord: RecordThread | null
    chatRecord: RecordThread | null
    showRequest: (request?: ChatRequest) => void
    clearPendingAsk: (id: string) => void
    clearPendingThreadId: () => void
    setRecordSlot: (el: HTMLElement | null) => void
    stageRecord: (record: RecordThread) => () => void
    forgetRecord: (claimId: string) => void
    showGeneralChat: () => void
}

function recordThreadFor({
    request,
    stagedRecord,
    chatRecord,
    isChatInFront,
}: {
    request: ChatRequest
    stagedRecord: RecordThread | null
    chatRecord: RecordThread | null
    isChatInFront: boolean
}): RecordThread | null {
    if (request.threadId) return null
    if (isChatInFront) return chatRecord
    return stagedRecord
}

export const useChatAppStore = create<ChatAppState>()(set => ({
    focusRequest: 0,
    pendingAsk: null,
    pendingThreadId: null,
    recordSlot: null,
    stagedRecord: null,
    chatRecord: null,
    showRequest: (request = {}) =>
        set(state => {
            const focusRequest = state.focusRequest + 1
            const question = request.question?.trim()
            return {
                focusRequest,
                pendingAsk: question
                    ? {
                          id: `ask-${focusRequest}`,
                          text: question,
                          ...(request.viewContext && {
                              viewContext: request.viewContext,
                          }),
                      }
                    : state.pendingAsk,
                pendingThreadId: request.threadId ?? state.pendingThreadId,
                chatRecord: recordThreadFor({
                    request,
                    stagedRecord: state.stagedRecord,
                    chatRecord: state.chatRecord,
                    isChatInFront: isChatInFront(),
                }),
            }
        }),
    clearPendingAsk: id =>
        set(state =>
            state.pendingAsk?.id === id ? { pendingAsk: null } : state
        ),
    clearPendingThreadId: () => set({ pendingThreadId: null }),
    setRecordSlot: recordSlot => set({ recordSlot }),
    stageRecord: record => {
        set({ stagedRecord: record })
        return () =>
            set(state =>
                state.stagedRecord?.claimId === record.claimId
                    ? { stagedRecord: null }
                    : state
            )
    },
    forgetRecord: claimId =>
        set(state => ({
            stagedRecord:
                state.stagedRecord?.claimId === claimId
                    ? null
                    : state.stagedRecord,
            chatRecord:
                state.chatRecord?.claimId === claimId ? null : state.chatRecord,
        })),
    showGeneralChat: () =>
        set(state =>
            state.chatRecord
                ? { chatRecord: null, pendingAsk: null }
                : { chatRecord: null }
        ),
}))

export function isChatInFront(): boolean {
    return (
        getStagedWindow(useDesktopStore.getState().windows)?.type ===
        CHAT_WINDOW_TYPE
    )
}

export function useIsChatInFront(): boolean {
    return useDesktopStore(
        state => getStagedWindow(state.windows)?.type === CHAT_WINDOW_TYPE
    )
}

export function openChatWindow(request: ChatRequest = {}) {
    if (!request.threadId && askInShipmentPanel(request.question)) return
    const desktop = useDesktopStore.getState()
    const staged = getStagedWindow(desktop.windows)
    const isLeavingPage = staged?.type !== CHAT_WINDOW_TYPE
    const viewContext =
        isLeavingPage && request.question?.trim()
            ? capturePageContext({
                  windows: desktop.windows,
                  highlightedText: takeHighlightedText(),
              })
            : undefined
    useChatAppStore
        .getState()
        .showRequest(viewContext ? { ...request, viewContext } : request)
    if (!isLeavingPage) return
    const data = { originWindowId: staged?.id }
    const existing = desktop.windows.find(w => w.type === CHAT_WINDOW_TYPE)
    if (existing) desktop.revealWindow(existing.id, { data })
    else desktop.openWindow(CHAT_WINDOW_TYPE, { data })
}

export function toggleChat() {
    if (isChatInFront()) useDesktopStore.getState().stageBack()
    else openChatWindow()
}

export function isChatShortcut(event: {
    metaKey: boolean
    ctrlKey: boolean
    key: string
}): boolean {
    return (event.metaKey || event.ctrlKey) && event.key === "j"
}

export function runChatShortcut({
    selectedText,
    askAbout,
}: {
    selectedText: string
    askAbout: (text: string) => void
}) {
    const text = selectedText.trim()
    if (text) askAbout(text)
    else toggleChat()
}
